import { PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { globalActions } from 'app/containers/Global/slice';
import {
  getPermissionNumbersFromBooleans,
  getPermissionsBooleansFromNumbers,
} from 'app/containers/User/slice/helpers';
import { RouteConstants } from 'app/routes';
import i18next from 'i18next';
import { generatePath } from 'react-router-dom';
import { put, takeLatest, delay, select } from 'redux-saga/effects';
import { ErrorCodeEnum, RootState } from 'types';
import { companyActions as actions } from '.';
import companyApi from '../companyApi';

import {
  ICompanyFormValues,
  ICompanyInList,
  ICreateCompany,
  IEditCompanyPayload,
  IUpdateCompany,
  IGetCompanyByIdPayload,
  IShareCompanyPayload,
  IDeleteShareCompanyPayload,
  ICompanyDetailsResponse,
  ICompanyDetails,
  IShareCompanyData,
} from './types';

function* getCompaniesSaga(action: PayloadAction<number>) {
  try {
    const envId = action.payload;

    const companies: ICompanyInList[] = yield companyApi.getCompanies(envId);

    yield put(actions.setCompanies(companies));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(actions.setCompanies([]));
  } finally {
    yield put(actions.setCompaniesLoading(false));
  }
}

function* getCompanyByIdSaga(action: PayloadAction<IGetCompanyByIdPayload>) {
  try {
    yield put(actions.setCompanyDetailsLoading(true));

    const { companyId, envId } = action.payload;

    const companyDetailsResponse: ICompanyDetailsResponse = yield companyApi.getCompanyById(
      envId,
      companyId,
    );

    const companyMappedData: ICompanyDetails = {
      ...companyDetailsResponse,
      companyHiikoEnvironments: companyDetailsResponse.companyHiikoEnvironments.map(
        env => ({
          hiikoEnvironment: env.hiikoEnvironment,
          accepted: env.accepted,
          role: env.role,
          permissions: getPermissionNumbersFromBooleans(env),
        }),
      ),
    };

    yield put(actions.setCompanyDetails(companyMappedData));
  } catch (err) {
    yield put(globalActions.handleError(err));

    const { envId } = action.payload;
    yield put(
      globalActions.setRedirectTo(
        generatePath(RouteConstants.company.list, {
          envId,
        }),
      ),
    );
  } finally {
    yield put(actions.setCompanyDetailsLoading(false));
  }
}

function* createCompanySaga(
  action: PayloadAction<{ envId: number; values: ICompanyFormValues }>,
) {
  try {
    yield put(actions.setCreateCompanyLoading(true));

    const { envId, values } = action.payload;

    const company: ICreateCompany = {
      ...values,
      hiikoEnvironmentId: envId,
    };

    yield companyApi.createOrUpdateCompany(envId, company);
    yield put(actions.getCompanies(envId));

    yield put(actions.toggleCompanyForm(false));

    yield delay(500);

    notification.success({
      message: i18next?.t('Company.Notification.Created', {
        defaultValue: 'Company "{{companyName}}" is created',
        companyName: company.name,
      }),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setCreateCompanyLoading(false));
  }
}

function* editCompanySaga(action: PayloadAction<IEditCompanyPayload>) {
  try {
    yield put(actions.setCreateCompanyLoading(true));
    const { id, values: form, envId } = action.payload;

    const company: IUpdateCompany = {
      ...form,
      id,
      hiikoEnvironmentId: envId,
    };

    // Warning: endpoint returns company without companyHiikoEnvironments
    yield companyApi.createOrUpdateCompany(envId, company);

    yield put(actions.getCompanyById({ envId, companyId: company.id }));
    yield put(actions.toggleCompanyForm(false));

    yield delay(500);

    notification.success({
      message: i18next?.t('Company.Notification.Updated', {
        defaultValue: 'Company "{{companyName}}" is updated',
        companyName: company.name,
      }),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setCreateCompanyLoading(false));
  }
}

function* shareCompanySaga(action: PayloadAction<IShareCompanyPayload>) {
  try {
    yield put(actions.setShareCompanyLoading(true));

    const { id, permissions, envId, companyId } = action.payload;

    const data: IShareCompanyData = {
      ...getPermissionsBooleansFromNumbers(permissions),
      hiikoEnvironmentId: Number(id),
    };

    yield companyApi.shareCompany(envId, companyId, data);

    yield put(actions.toggleShareCompanyForm(false));

    yield put(actions.getCompanyById({ envId, companyId }));

    yield delay(500);

    notification.success({
      message: i18next?.t(
        'Company.Notification.CompanyCreated',
        'Company  will get new access rights',
      ),
      placement: 'bottomRight',
    });
  } catch (err) {
    if (err?.data?.errorCode === ErrorCodeEnum.hikoEnvNotFound) {
      notification.error({
        message: i18next?.t(
          'Company.Notification.ShareEnvNotFound',
          'Hikos enviroment by provided id not found',
        ),
        placement: 'bottomRight',
      });
    } else {
      yield put(globalActions.handleError(err));
    }
  } finally {
    yield put(actions.setShareCompanyLoading(false));
  }
}

function* deleteShareCompanySaga(
  action: PayloadAction<IDeleteShareCompanyPayload>,
) {
  try {
    yield put(actions.setDeleteShareCompanyLoading(true));
    const { guestEnvId, companyId, envId } = action.payload;
    const guestEnvName = yield select(
      (state: RootState) =>
        state.company?.companyDetails.company?.companyHiikoEnvironments.find(
          companyEnv => companyEnv.hiikoEnvironment.id === guestEnvId,
        )?.hiikoEnvironment.name,
    ) || '';

    yield companyApi.deleteCompanySharing({ envId, guestEnvId, companyId });

    yield put(actions.toggleShareCompanyForm(false));

    yield put(actions.getCompanyById({ envId, companyId }));

    yield delay(500);

    notification.success({
      message: i18next?.t('Company.Notification.CompanyRemoveFromEnv', {
        defaultValue:
          'Company is successfully removed from "{{envName}}" environment.',
        envName: guestEnvName,
      }),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setDeleteShareCompanyLoading(false));
  }
}
export function* companySaga() {
  yield takeLatest(actions.deleteShareCompany.type, deleteShareCompanySaga);

  yield takeLatest(actions.getCompanies.type, getCompaniesSaga);
  yield takeLatest(actions.getCompanyById.type, getCompanyByIdSaga);

  yield takeLatest(actions.createCompany.type, createCompanySaga);
  yield takeLatest(actions.editCompany.type, editCompanySaga);
  yield takeLatest(actions.shareCompany.type, shareCompanySaga);
}
